/**
 * Headless props for a past-challenge card: title, date range, participant
 * avatars and the member's own outcome, mapped once so web and mobile render
 * the same product knowledge with their own primitives. Platform visuals
 * (icons, colors, map tiles) stay in each client.
 */
import { parseCalendarDate } from "@healthos/shared";
import { ordinal } from "./challenge-standing.js";
function formatDay(date, withYear) {
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        ...(withYear ? { year: "numeric" } : {}),
    });
}
/**
 * How long the completion modal may still open after the finish: the end
 * date itself and the five calendar days that follow. Closing the modal
 * hides it sooner; this window is only the outer bound.
 */
const JUST_ENDED_WINDOW_DAYS = 5;
/**
 * The newest past challenge whose end is at most five calendar days behind
 * `now`, for the completion modal on the live challenges page. Null when
 * nothing just ended.
 */
export function recentlyEndedChallenge(challenges, now = new Date()) {
    if (!challenges?.length)
        return null;
    const newest = [...challenges].sort((left, right) => right.endsOn.localeCompare(left.endsOn))[0];
    if (!newest)
        return null;
    const ended = parseCalendarDate(newest.endsOn);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysSinceEnd = Math.round((today.getTime() - ended.getTime()) / 86_400_000);
    return daysSinceEnd >= 0 && daysSinceEnd <= JUST_ENDED_WINDOW_DAYS ? newest : null;
}
/** "Jul 1 to Jul 31, 2026"; both years appear when the window crosses one. */
export function pastChallengeDateRange(startsOn, endsOn) {
    const start = parseCalendarDate(startsOn);
    const end = parseCalendarDate(endsOn);
    const crossesYear = start.getFullYear() !== end.getFullYear();
    return `${formatDay(start, crossesYear)} to ${formatDay(end, true)}`;
}
export function pastChallengeCardProps(summary) {
    const { viewer } = summary;
    return {
        id: summary.id,
        title: summary.name,
        routeName: summary.routeName,
        dateRange: pastChallengeDateRange(summary.startsOn, summary.endsOn),
        participantCount: summary.participantCount,
        avatars: summary.participantsPreview,
        extraParticipants: Math.max(0, summary.participantCount - summary.participantsPreview.length),
        rankLabel: viewer.rank !== null ? `${ordinal(viewer.rank)} of ${summary.participantCount}` : null,
        stepsLabel: viewer.steps > 0 ? `${viewer.steps.toLocaleString()} steps` : null,
        finishedRoute: viewer.finishedRoute,
        progressFraction: viewer.progressFraction,
        map: {
            routePath: summary.routePath,
            routeMode: summary.routeMode,
            mapCenter: summary.mapCenter,
            mapZoom: summary.mapZoom,
        },
    };
}
//# sourceMappingURL=past-challenge-card.js.map